import React, { useState } from 'react';
import { CheckIcon, DownloadIcon, LoaderIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { AdminPageTitle, ConfirmationModal, DateRange, Panel, PermissionGate, StatusBadge } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { exportJobs, exportTemplates, permissionMatrix, recordAudit, type ExportJob, type ExportScope } from '../../data/admin';
import { prototypeAdminRole } from '../../data/adminOperations';
import { useLocale } from '../../contexts/LocaleContext';
const statusLabels: Record<string, string> = {
  ready: 'Pronto',
  processing: 'Processando',
  failed: 'Falhou'
};

/** Privacy notice per export scope — never a generic "all anonymous" claim. */
const scopeNotice: Record<ExportScope, string> = {
  aggregate: 'Contém apenas dados agregados, sem identificação de usuários.',
  operational: 'Contém registros operacionais identificáveis (itens moderados, identificadores de conteúdo e datas). Exige permissão de exportação.',
  audit: 'Pode incluir administradores responsáveis, identificadores de entidades, data e hora e o contexto de cada ação. Exige permissão de exportação de Auditoria e Relatórios.'
};
const HEALTH_NOTICE = 'Dados sensíveis de saúde (biomarcadores, testosterona, composição corporal e valores individuais de hábitos) nunca entram em exportações operacionais.';
function canExport(scope: ExportScope): boolean {
  const perms = permissionMatrix[prototypeAdminRole];
  const reports = (perms.reports ?? []).includes('export');
  if (scope === 'audit') return reports && (perms.audit ?? []).includes('export');
  return reports;
}
export function AdminExports() {
  const {
    tl
  } = useLocale();
  const [range, setRange] = useState('30d');
  const [template, setTemplate] = useState(exportTemplates[0].id);
  const [format, setFormat] = useState<'CSV' | 'XLSX' | 'PDF'>('CSV');
  const [confirm, setConfirm] = useState(false);
  const [jobs, setJobs] = useState<ExportJob[]>(exportJobs);
  const selected = exportTemplates.find(item => item.id === template)!;
  const allowed = canExport(selected.scope);
  const columns: Column<ExportJob>[] = [{
    id: 'name',
    header: 'Relatório',
    width: '30%',
    cell: row => <span className="min-w-0" data-id="element-1631" data-mp="25m4kncq9dxtp">
          <span className="block text-[12.5px] font-medium text-deep" data-id="element-1632" data-mp="337ti2uyd3rjd">{tl(row.name)}</span>
          <span className="bee-num block text-[11px] text-muted" data-id="element-1633" data-mp="2y7xe2vpo003t">{row.range}</span>
        </span>
  }, {
    id: 'format',
    header: 'Formato',
    sortValue: row => row.format,
    cell: row => <span className="bee-num rounded-pill border border-sand-line bg-white px-2 py-[2px] text-[10.5px] font-bold text-deep" data-id="element-1634" data-mp="2ohm68y2v16rn">
          {row.format}
        </span>
  }, {
    id: 'by',
    header: 'Solicitado por',
    cell: row => <span className="min-w-0" data-id="element-1635" data-mp="3oipoiq8mx2s9">
          <span className="block text-[12.5px] text-deep" data-id="element-1636" data-mp="3ojiys16syaf5">{row.by}</span>
          <span className="block text-[11px] text-muted" data-id="element-1637" data-mp="3th0xyzv0pb9y">{row.requested}</span>
        </span>
  }, {
    id: 'size',
    header: 'Tamanho',
    align: 'right',
    cell: row => <span className="bee-num text-muted" data-id="element-1638" data-mp="7a2rkan4elvr">{row.size ?? '—'}</span>,
    optional: true
  }, {
    id: 'status',
    header: 'Status',
    cell: row => <span className="flex items-center gap-2" data-id="element-1639" data-mp="b9uykiwv8jz0">
          {row.status === 'ready' ? <CheckIcon size={14} className="text-soft" aria-hidden="true" data-id="element-1640" data-mp="g8xvfg2ozjvw" /> : row.status === 'processing' ? <LoaderIcon size={14} className="text-gold-deep" aria-hidden="true" data-id="element-1641" data-mp="q0u5lpi1z0gh" /> : <XIcon size={14} className="text-gold-deep" aria-hidden="true" data-id="element-1642" data-mp="uzx2gmnvq0dd" />}
          <StatusBadge status={row.status} label={statusLabels[row.status]} data-id="element-1643" data-mp="uy3rwwa5aw03" />
        </span>
  }];
  return <div className="space-y-4" data-id="element-1644" data-mp="1wq515kwwe9t7">
      <AdminPageTitle title="Relatórios" subtitle="Exportações operacionais com filtro de período, escopo de dados por tipo de relatório e status claro de processamento. Toda exportação fica registrada na auditoria." action={<DateRange value={range} onChange={setRange} data-id="element-1646" data-mp="25kjehhxvba9o" />} data-id="element-1645" data-mp="1wpd6qdf1ei4i" />

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1fr_1.4fr]" data-id="element-1647" data-mp="21iel8kbc0fnk">
        <Panel title="Nova exportação" data-id="element-1648" data-mp="1dr0x6qk7r68e">
          <label className="block" data-id="element-1649" data-mp="1drsrly22qxx3">
            <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-1650" data-mp="18sfwkq11ukjf">Relatório</span>
            <select value={template} onChange={event => setTemplate(event.target.value)} className="mt-1 h-11 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-1651" data-mp="1ms7dayiyaf14">
              {exportTemplates.map(item => <option key={item.id} value={item.id} data-id="element-1652" data-mp="2t79fcfyhcysp">
                  {tl(item.label)}
                </option>)}
            </select>
          </label>
          <p className="mt-2 text-[11.5px] leading-snug text-muted" data-id="element-1653" data-mp="35qn764rgg2am">{tl(selected.hint)}</p>

          <fieldset className="mt-3" data-id="element-1654" data-mp="2rr40lgeakz0f">
            <legend className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-1655" data-mp="2rs5lsdp0v1vv">Formato</legend>
            <div className="mt-1 flex gap-1" data-id="element-1656" data-mp="2wr9ul381axlo">
              {(['CSV', 'XLSX', 'PDF'] as const).map(option => <button key={option} type="button" aria-pressed={format === option} onClick={() => setFormat(option)} className={`h-10 flex-1 rounded-card border text-[12.5px] font-bold transition-colors duration-150 ease-brand ${format === option ? 'border-gold bg-gold text-deep' : 'border-sand-line bg-white text-muted hover:text-deep'}`} data-id="element-1657" data-mp="3kh49rrftsy93">
                  {option}
                </button>)}
            </div>
          </fieldset>

          <div className="mt-3" data-id="element-1658" data-mp="ltlxdc133str">
            <PermissionGate allowed={allowed} area={selected.scope === 'audit' ? 'exportação de auditoria' : 'exportação de relatórios'} data-id="element-1659" data-mp="gwlv7c7cva7x">
              <Button variant="gold" fullWidth onClick={() => setConfirm(true)} data-id="element-1660" data-mp="7wgvzpcbfi4u">
                <DownloadIcon size={16} data-id="element-1661" data-mp="7yjoqh4urpd9" />
                Solicitar exportação
              </Button>
            </PermissionGate>
          </div>

          <div className="mt-3 space-y-1.5" data-id="element-1662" data-mp="3uaqitu0lki3i">
            <p className="flex items-start gap-2 text-[11.5px] leading-snug text-deep" data-id="element-1663" data-mp="3u9yn8j702kd8">
              <ShieldCheckIcon size={14} className="mt-[1px] shrink-0 text-gold" aria-hidden="true" data-id="element-1664" data-mp="3pam1k1rv7jc8" />
              {scopeNotice[selected.scope]}
            </p>
            <p className="pl-[22px] text-[11.5px] leading-snug text-muted" data-id="element-1665" data-mp="3gayxt9bny1g0">{HEALTH_NOTICE}</p>
            <p className="pl-[22px] text-[11.5px] leading-snug text-muted" data-id="element-1666" data-mp="3bcns9aseid3l">
              A própria exportação é registrada na auditoria com quem solicitou, o relatório, o
              período e o formato.
            </p>
          </div>
        </Panel>

        <DataTable rows={jobs} columns={columns} rowKey={row => row.id} pageSize={8} emptyTitle="Nenhuma exportação" emptyBody="As exportações solicitadas aparecem aqui com o status de processamento." actions={[{
        id: 'download',
        label: 'Baixar arquivo',
        onSelect: () => undefined
      }, {
        id: 'retry',
        label: 'Reprocessar',
        onSelect: row => setJobs(prev => prev.map(job => job.id === row.id ? {
          ...job,
          status: 'processing'
        } : job))
      }]} data-id="element-1667" data-mp="1vvss74piwjhn" />
      </div>

      <ConfirmationModal open={confirm} title="Solicitar esta exportação?" body={`${tl(selected.label)} em ${format}, no período selecionado. ${scopeNotice[selected.scope]} A solicitação será registrada na auditoria.`} confirmLabel="Solicitar" onConfirm={() => {
      if (!allowed) return;
      setJobs(prev => [{
        id: `e${prev.length + 1}`,
        name: selected.label,
        format,
        range: '01/09 – 22/09',
        status: 'processing',
        requested: 'agora',
        by: 'Bruno Sato'
      }, ...prev]);
      recordAudit({
        action: {
          'pt-BR': 'Solicitou exportação',
          'en-US': 'Requested export'
        },
        entity: `export:${selected.id}`,
        context: {
          'pt-BR': `${selected.label['pt-BR']} · ${format} · ${range}`,
          'en-US': `${selected.label['en-US']} · ${format} · ${range}`
        },
        severity: selected.scope === 'aggregate' ? 'info' : 'sensitive'
      });
      setConfirm(false);
    }} onClose={() => setConfirm(false)} data-id="element-1668" data-mp="tf7p3jj9g3we" />
    </div>;
}